import { packs, offlineSupported } from "./offlineStore";
import type { AvailableYear, TopicWithMastery } from "../api/catalog";

/** A question as a downloaded pack holds it. */
export interface PackQuestion {
  id: string;
  topicId: string | null;
  topicName?: string | null;
  examYear: number | null;
}

async function questionsFor(subjectId: string): Promise<PackQuestion[] | null> {
  if (!offlineSupported() || !subjectId) return null;
  const pack = await packs.get(subjectId).catch(() => undefined);
  if (!pack || pack.questions.length === 0) return null;
  return pack.questions as PackQuestion[];
}

/** The ids of every subject with a downloaded pack for the active exam. */
export async function downloadedSubjectIds(): Promise<Set<string>> {
  if (!offlineSupported()) return new Set();
  const all = await packs.all().catch(() => []);
  return new Set(all.filter((p) => p.questions.length > 0).map((p) => p.subjectId));
}

/** Years in a downloaded pack, newest first, with how many questions each has. */
export async function packYears(subjectId: string): Promise<{ years: AvailableYear[] } | null> {
  const questions = await questionsFor(subjectId);
  if (!questions) return null;
  const counts = new Map<number, number>();
  for (const q of questions) {
    if (q.examYear) counts.set(q.examYear, (counts.get(q.examYear) ?? 0) + 1);
  }
  return {
    years: [...counts.entries()]
      .sort(([a], [b]) => b - a)
      .map(([year, questionCount]) => ({ year, questionCount })),
  };
}

/**
 * Topics in a downloaded pack. Mastery is unknown offline — it lives on the
 * server — so every topic reads as not started rather than inventing a score.
 */
export async function packTopics(
  subjectId: string,
): Promise<{ topics: TopicWithMastery[] } | null> {
  const questions = await questionsFor(subjectId);
  if (!questions) return null;
  const byTopic = new Map<string, { name: string; count: number }>();
  for (const q of questions) {
    if (!q.topicId || !q.topicName) continue;
    const row = byTopic.get(q.topicId) ?? { name: q.topicName, count: 0 };
    row.count += 1;
    byTopic.set(q.topicId, row);
  }
  return {
    topics: [...byTopic.entries()]
      .sort(([, a], [, b]) => a.name.localeCompare(b.name))
      .map(([id, t]) => ({ id, name: t.name, mastery: null, weak: false, questionCount: t.count })),
  };
}

/**
 * Questions for an offline sitting, honouring what the student picked: a year,
 * a topic, or neither. Shuffled, so practising the same pack twice is not the
 * same ten in the same order. Null when nothing is downloaded for the choice.
 */
export async function packSession<T extends PackQuestion>(
  subjectId: string,
  options: { topicIds?: string[] | undefined; examYear?: number | undefined },
  size: number,
): Promise<T[] | null> {
  const questions = (await questionsFor(subjectId)) as T[] | null;
  if (!questions) return null;
  const topics = options.topicIds?.length ? new Set(options.topicIds) : null;
  const picked = questions.filter(
    (q) =>
      (topics === null || (q.topicId !== null && topics.has(q.topicId))) &&
      (options.examYear === undefined || q.examYear === options.examYear),
  );
  if (picked.length === 0) return null;
  const shuffled = [...picked];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j]!, shuffled[i]!];
  }
  return shuffled.slice(0, size);
}

/** "1.4 MB", "320 KB", "0 MB" — what the downloads take on this phone. */
export function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return bytes > 0 ? `${bytes} B` : "0 MB";
}

/**
 * The total size of every downloaded pack, for both exams — it is the phone's
 * storage being described. 0 when none, or storage is unavailable.
 */
export async function downloadedBytes(): Promise<number> {
  if (!offlineSupported()) return 0;
  const all = await packs.everything().catch(() => []);
  return all.reduce((sum, pack) => sum + (pack.bytes || 0), 0);
}
